import React,{useRef,useState} from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiEye,FiEdit,FiMaximize,FiMinimize,FiBold,FiItalic,FiLink,FiList,FiListOrdered,FiMinus,FiMessageSquare,FiXCircle,FiExternalLink}=FiIcons;

const BLOCK_TYPE_OPTIONS=[
  {value:'body',label:'Paragraph'},
  {value:'h1',label:'Title (H1)'},
  {value:'h2',label:'Section (H2)'},
  {value:'h3',label:'Subsection (H3)'},
];
const FONT_OPTIONS=[
  {value:'',label:'Default font'},
  {value:'helvetica',label:'Arial / Modern Sans'},
  {value:'times',label:'Georgia / Classic Serif'},
  {value:'courier',label:'Courier New / Monospace'},
];
const SIZE_OPTIONS=['','12','14','16','18','20','24','30'];
const LINE_SPACING_OPTIONS=['1','1.15','1.5','2','2.5','3'];
const COLOR_PRESETS=[
  {value:'',label:'Default colour'},
  {value:'#0f172a',label:'BPA Navy'},
  {value:'#705124',label:'BPA Gold'},
  {value:'#475569',label:'Slate'},
  {value:'#b42318',label:'Red'},
  {value:'#15803d',label:'Green'},
];
const BLOCK_STYLE_RE=/\s*\{::\s*([^}]*)\}\s*$/;

const escapeHtml=(value)=> String(value || '')
  .replace(/&/g,'&amp;')
  .replace(/</g,'&lt;')
  .replace(/>/g,'&gt;')
  .replace(/"/g,'&quot;')
  .replace(/'/g,'&#39;');

const sanitizeLinkUrl=(value)=> {
  if (!value) return '#';
  try {
    const protocol=new URL(value,window.location.href).protocol;
    if (['https:','http:','mailto:'].includes(protocol)) return value;
  } catch (error) {
    console.warn('URL parsing error:',error);
  }
  return '#';
};

const normalizeDocumentOutput=(value='')=> String(value)
  .replace(/\u2014/g,'-')
  .replace(/(^|[\s(])_([^_\n]+?)_(?=$|[\s).,;:!?])/g,'$1*$2*');

const parseBlockStyle=(rawLine='')=> {
  const match=String(rawLine).match(BLOCK_STYLE_RE);
  const style={};
  if (!match) return {text:String(rawLine),style};
  for (const token of match[1].trim().split(/\s+/)) {
    const [key,value]=token.split('=');
    if (!key || value==null) continue;
    if (key==='font' && ['helvetica','times','courier'].includes(value)) style.font=value;
    if (key==='size' && ['12','14','16','18','20','24','30'].includes(value)) style.size=value;
    if (key==='color' && /^#[0-9a-fA-F]{6}$/.test(value)) style.color=value.toLowerCase();
    if (key==='line' && LINE_SPACING_OPTIONS.includes(value)) style.line=value;
    if ((key==='before' || key==='after') && ['0','1'].includes(value)) style[key]=value;
  }
  return {text:String(rawLine).replace(match[0],'').trimEnd(),style};
};

const blockStyleToCss=(style={})=> {
  const declarations=[];
  if (style.font==='helvetica') declarations.push('font-family:Arial,Helvetica,sans-serif');
  if (style.font==='times') declarations.push("font-family:Georgia,'Times New Roman',serif");
  if (style.font==='courier') declarations.push("font-family:'Courier New',Courier,monospace");
  if (style.size) declarations.push(`font-size:${style.size}px`);
  if (style.color) declarations.push(`color:${style.color}`);
  if (style.line) declarations.push(`line-height:${style.line}`);
  if (style.before==='1') declarations.push('margin-top:12px');
  if (style.before==='0') declarations.push('margin-top:0');
  if (style.after==='1') declarations.push('margin-bottom:12px');
  if (style.after==='0') declarations.push('margin-bottom:0');
  return declarations.join(';');
};

const styleAttr=(style)=> {
  const css=blockStyleToCss(style);
  return css ? ` style="${css}"` : '';
};

const renderInlineMarkdown=(value)=> {
  if (!value) return '';
  const normalized=normalizeDocumentOutput(value).replace(/(\d)\\\./g,'$1.');
  let escaped=escapeHtml(normalized);
  escaped=escaped.replace(/`([^`]+)`/g,'<code class="px-1 py-0.5 rounded bg-slate-100 text-[0.9em]">$1</code>');
  escaped=escaped.replace(/\*\*(.+?)\*\*/g,'<strong class="font-semibold">$1</strong>');
  escaped=escaped.replace(/\*(.+?)\*/g,'<em>$1</em>');
  escaped=escaped.replace(/\[(.+?)\]\((.+?)\)/g,(match,text,url)=> {
    const safeUrl=sanitizeLinkUrl(url);
    return `<a href="${escapeHtml(safeUrl)}" class="text-[#705124] underline underline-offset-4 hover:text-[#8c6a2f]" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });
  return escaped;
};

const renderMarkdownBlocks=(markdown,{published=false}={})=> {
  if (!markdown || !markdown.trim()) return '<p class="text-slate-500">No content available.</p>';
  const blocks=[];
  let currentList=null;
  let listItems=[];

  const flushList=()=> {
    if (!currentList || !listItems.length) return;
    const listClass=published
      ? (currentList==='ul' ? 'list-disc pl-6 my-5 space-y-2 text-slate-700 leading-7' : 'list-decimal pl-6 my-5 space-y-2 text-slate-700 leading-7')
      : (currentList==='ul' ? 'list-disc pl-6 my-4 space-y-1.5' : 'list-decimal pl-6 my-4 space-y-1.5');
    const items=listItems.map(({text,style})=> `<li class="${published ? 'pl-1' : 'text-slate-700 leading-7'}"${styleAttr(style)}>${renderInlineMarkdown(text)}</li>`).join('');
    blocks.push(`<${currentList} class="${listClass}">${items}</${currentList}>`);
    currentList=null;
    listItems=[];
  };

  for (const rawLine of markdown.split('\n')) {
    const parsed=parseBlockStyle(rawLine.trim());
    const line=parsed.text.trim();
    const style=parsed.style;
    if (!line) {flushList();continue;}
    if (line.startsWith('### ')) {
      flushList();
      const classes=published ? 'text-lg sm:text-xl font-semibold tracking-tight mt-9 mb-3 text-slate-900' : 'text-lg font-semibold mt-7 mb-2 text-slate-900';
      blocks.push(`<h3 class="${classes}"${styleAttr(style)}>${renderInlineMarkdown(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      const classes=published ? 'text-xl sm:text-2xl font-semibold tracking-tight mt-12 mb-4 text-slate-900 pb-3 border-b border-slate-200' : 'text-xl font-semibold mt-9 mb-3 text-slate-900 border-b border-slate-200 pb-2';
      blocks.push(`<h2 class="${classes}"${styleAttr(style)}>${renderInlineMarkdown(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith('# ')) {
      flushList();
      const classes=published ? 'text-2xl sm:text-3xl font-semibold tracking-tight mt-10 mb-5 text-slate-950' : 'text-3xl font-bold mt-8 mb-4 text-slate-950';
      blocks.push(`<h1 class="${classes}"${styleAttr(style)}>${renderInlineMarkdown(line.slice(2))}</h1>`);
      continue;
    }
    if (line==='---') {flushList();blocks.push(`<hr class="${published ? 'my-10' : 'my-6'} border-slate-200" />`);continue;}
    if (line.startsWith('> ')) {
      flushList();
      const classes=published ? 'my-6 border-l-4 border-[#d7bb79] bg-[#fbf8f1] px-5 py-4 text-slate-700 leading-7 rounded-r-lg' : 'my-5 border-l-4 border-[#d7bb79] bg-[#fbf8f1] px-4 py-3 text-slate-700 leading-7 rounded-r-lg';
      blocks.push(`<blockquote class="${classes}"${styleAttr(style)}>${renderInlineMarkdown(line.slice(2))}</blockquote>`);
      continue;
    }
    const bullet=line.match(/^(?:-|•|●|▪)\s+(.+)$/);
    if (bullet) {if (currentList!=='ul') {flushList();currentList='ul';}listItems.push({text:bullet[1],style});continue;}
    const number=line.match(/^\d+\.\s+(.+)$/);
    if (number) {if (currentList!=='ol') {flushList();currentList='ol';}listItems.push({text:number[1],style});continue;}
    flushList();
    const classes=published ? 'my-5 text-[15px] sm:text-base text-slate-700 leading-7 sm:leading-8' : 'mb-4 text-slate-700 leading-7';
    blocks.push(`<p class="${classes}"${styleAttr(style)}>${renderInlineMarkdown(line)}</p>`);
  }
  flushList();
  return blocks.length ? blocks.join('') : '<p class="text-slate-500">No content available.</p>';
};

const parseStyleTokens=(raw='')=> {
  const style={};
  for (const token of raw.trim().split(/\s+/)) {
    const [key,value]=token.split('=');
    if (key && value!=null) style[key]=value;
  }
  return style;
};

const serializeStyleTokens=(style)=> {
  const tokens=[];
  if (style.font) tokens.push(`font=${style.font}`);
  if (style.size) tokens.push(`size=${style.size}`);
  if (style.color) tokens.push(`color=${style.color}`);
  if (style.line) tokens.push(`line=${style.line}`);
  if (style.before) tokens.push(`before=${style.before}`);
  if (style.after) tokens.push(`after=${style.after}`);
  return tokens.length ? ` {:: ${tokens.join(' ')}}` : '';
};

const stripMarkdownForPreview=(value='')=> normalizeDocumentOutput(value)
  .replace(BLOCK_STYLE_RE,'')
  .replace(/^#{1,6}\s+/,'')
  .replace(/\*\*/g,'')
  .replace(/\*/g,'')
  .replace(/`/g,'')
  .replace(/^>\s+/,'')
  .trim();

const derivePreviewTitle=(content='')=> {
  const lines=String(content).split('\n').map(stripMarkdownForPreview).filter(Boolean);
  const firstNonBrand=lines.find((line)=> !/^BPA\s+SOLUTIONS$/i.test(line));
  return firstNonBrand || 'Document Preview';
};

const collectPreviewStyles=()=> Array.from(document.head.querySelectorAll('link[rel="stylesheet"],style'))
  .map((node)=> {
    if (node.tagName==='LINK') {
      const href=node.href || node.getAttribute('href') || '';
      return href ? `<link rel="stylesheet" href="${escapeHtml(href)}" />` : '';
    }
    return node.outerHTML;
  })
  .filter(Boolean)
  .join('\n');

export default function RichTextEditor({content,onChange,readOnly=false}) {
  const [mode,setMode]=useState('edit');
  const [isFullscreen,setIsFullscreen]=useState(false);
  const [linkDialog,setLinkDialog]=useState({open:false,text:'',url:''});
  const [blockTypeChoice,setBlockTypeChoice]=useState('body');
  const [fontChoice,setFontChoice]=useState('');
  const [sizeChoice,setSizeChoice]=useState('');
  const [colorChoice,setColorChoice]=useState('');
  const [spacingChoice,setSpacingChoice]=useState('');
  const textareaRef=useRef(null);
  const selectionRef=useRef({start:0,end:0});

  const captureSelection=()=> {
    const textarea=textareaRef.current;
    if (!textarea) return;
    selectionRef.current={start:textarea.selectionStart,end:textarea.selectionEnd};
  };

  const getSelectionRange=()=> {
    const textarea=textareaRef.current;
    if (textarea) return {start:textarea.selectionStart,end:textarea.selectionEnd};
    return selectionRef.current;
  };

  const replaceSelection=(replacement,start,end,newCursorPos)=> {
    const newContent=content.substring(0,start)+replacement+content.substring(end);
    const cursor=newCursorPos ?? (start+replacement.length);
    selectionRef.current={start:cursor,end:cursor};
    onChange(newContent);
    setTimeout(()=> {
      const textarea=textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(cursor,cursor);
    },0);
  };

  const transformSelectedLines=(transform)=> {
    if (readOnly || mode==='preview') return;
    const {start:selectionStart,end:selectionEnd}=getSelectionRange();
    const lineStart=content.lastIndexOf('\n',Math.max(0,selectionStart-1))+1;
    const nextBreak=content.indexOf('\n',selectionEnd);
    const lineEnd=nextBreak===-1 ? content.length : nextBreak;
    const segment=content.substring(lineStart,lineEnd);
    const transformed=segment.split('\n').map(transform).join('\n');
    const newContent=content.substring(0,lineStart)+transformed+content.substring(lineEnd);
    selectionRef.current={start:lineStart,end:lineStart+transformed.length};
    onChange(newContent);
    setTimeout(()=> {
      const textarea=textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(selectionRef.current.start,selectionRef.current.end);
    },0);
  };

  const applyBlockStyle=(key,value)=> {
    transformSelectedLines((line)=> {
      if (!line.trim()) return line;
      const match=line.match(BLOCK_STYLE_RE);
      const style=parseStyleTokens(match?.[1] || '');
      if (value) style[key]=value;
      else delete style[key];
      const base=match ? line.replace(match[0],'').trimEnd() : line.trimEnd();
      return `${base}${serializeStyleTokens(style)}`;
    });
  };

  const applySpacingChoice=(value)=> {
    setSpacingChoice(value);
    if (!value) return;
    if (value.startsWith('line:')) {
      applyBlockStyle('line',value.slice(5));
    } else if (value==='before:add') {
      applyBlockStyle('before','1');
    } else if (value==='before:remove') {
      applyBlockStyle('before','0');
    } else if (value==='after:add') {
      applyBlockStyle('after','1');
    } else if (value==='after:remove') {
      applyBlockStyle('after','0');
    } else if (value==='spacing:reset') {
      transformSelectedLines((line)=> {
        if (!line.trim()) return line;
        const match=line.match(BLOCK_STYLE_RE);
        const style=parseStyleTokens(match?.[1] || '');
        delete style.line;
        delete style.before;
        delete style.after;
        const base=match ? line.replace(match[0],'').trimEnd() : line.trimEnd();
        return `${base}${serializeStyleTokens(style)}`;
      });
    }
    setTimeout(()=>setSpacingChoice(''),0);
  };

  const applyBlockType=(value)=> {
    setBlockTypeChoice(value);
    transformSelectedLines((line)=> {
      if (!line.trim()) return line;
      const match=line.match(BLOCK_STYLE_RE);
      const style=parseStyleTokens(match?.[1] || '');
      const base=(match ? line.replace(match[0],'').trimEnd() : line.trimEnd()).replace(/^#{1,6}\s+/,'');
      const prefix=value==='h1' ? '# ' : value==='h2' ? '## ' : value==='h3' ? '### ' : '';
      return `${prefix}${base}${serializeStyleTokens(style)}`;
    });
  };

  const clearBlockFormatting=()=> transformSelectedLines((line)=> line.replace(BLOCK_STYLE_RE,'').trimEnd());

  const handleToolbarAction=(action)=> {
    if (readOnly || mode==='preview') return;
    const {start,end}=getSelectionRange();
    const selectedText=content.substring(start,end);
    switch (action) {
      case 'bold': return replaceSelection(`**${selectedText || 'bold text'}**`,start,end);
      case 'italic': return replaceSelection(`*${selectedText || 'italic text'}*`,start,end);
      case 'link': setLinkDialog({open:true,text:selectedText,url:''});return;
      case 'ul': {
        const lines=selectedText.split('\n').filter((line)=> line.trim());
        const formatted=lines.length ? lines.map((line)=> `- ${line}`).join('\n') : '- List item';
        return replaceSelection(formatted,start,end);
      }
      case 'ol': {
        const lines=selectedText.split('\n').filter((line)=> line.trim());
        const formatted=lines.length ? lines.map((line,index)=> `${index+1}. ${line}`).join('\n') : '1. List item';
        return replaceSelection(formatted,start,end);
      }
      case 'quote': return transformSelectedLines((line)=> line.trim() ? (line.trimStart().startsWith('> ') ? line : `> ${line}`) : line);
      case 'hr': return replaceSelection('\n\n---\n\n',start,end,start+8);
      default: return undefined;
    }
  };

  const insertLink=()=> {
    if (!linkDialog.url) return;
    const {start,end}=getSelectionRange();
    const linkText=linkDialog.text || linkDialog.url;
    const linkMarkdown=`[${linkText}](${linkDialog.url})`;
    replaceSelection(linkMarkdown,start,end);
    setLinkDialog({open:false,text:'',url:''});
  };

  const handlePaste=(event)=> {
    if (readOnly) return;
    const text=event.clipboardData?.getData('text/plain');
    if (text==null) return;
    event.preventDefault();
    const normalized=text
      .replace(/\u0000/g,'')
      .replace(/\u00A0/g,' ')
      .replace(/\r\n/g,'\n')
      .replace(/\r/g,'\n');
    const textarea=textareaRef.current;
    if (!textarea) {onChange(content+normalized);return;}
    replaceSelection(normalized,textarea.selectionStart,textarea.selectionEnd);
  };

  const formattingDisabled=readOnly || mode==='preview';
  const toolbarButtonClass='h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';
  const toolbarSelectClass='h-9 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 outline-none hover:border-slate-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed';
  const dividerClass='hidden sm:block w-px h-6 bg-slate-200 mx-1';
  const previewTitle=derivePreviewTitle(content);

  const preserveSelection=(event)=> {
    if (formattingDisabled) return;
    captureSelection();
    event.preventDefault();
  };

  const openPublishedPreview=()=> {
    const previewWindow=window.open('','_blank');
    if (!previewWindow) {
      window.alert('The preview tab was blocked by your browser. Please allow pop-ups for the Legal Vault and try again.');
      return;
    }

    try {previewWindow.opener=null;} catch (error) {console.warn('Unable to detach preview opener:',error);}

    const safeTitle=escapeHtml(previewTitle);
    const styleAssets=collectPreviewStyles();
    const publishedBody=renderMarkdownBlocks(content,{published:true});

    previewWindow.document.open();
    previewWindow.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${safeTitle} · Draft Published Preview</title>
  ${styleAssets}
  <style>html,body{margin:0;min-height:100%;background:#f8fafc}body{-webkit-font-smoothing:antialiased}</style>
</head>
<body class="bg-slate-50 text-slate-900">
  <main class="min-h-screen px-3 py-6 sm:px-6 sm:py-10">
    <article class="max-w-6xl mx-auto bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
      <div class="h-1 bg-gradient-to-r from-[#8c6a2f] via-[#d7bb79] to-[#8c6a2f]"></div>
      <header class="bg-[#0f172a] text-white px-6 sm:px-9 py-6 sm:py-7">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#d7bb79]">BPA Solutions</p>
            <p class="text-sm text-slate-300 mt-1">Published Contract &amp; Policy Document</p>
          </div>
          <span class="self-start sm:self-auto inline-flex items-center px-3 py-1.5 rounded-full border text-xs font-semibold bg-blue-400/10 border-blue-300/25 text-blue-200">Draft preview · not published</span>
        </div>
      </header>
      <section class="px-6 sm:px-9 py-8 sm:py-10 border-b border-slate-200">
        <div class="max-w-4xl">
          <div class="flex flex-wrap items-center gap-2 mb-4">
            <span class="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8c6a2f]">Legal document</span>
            <span class="text-slate-300">•</span>
            <span class="text-xs font-medium text-slate-500">Draft preview</span>
          </div>
          <h1 class="text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-tight text-slate-950">${safeTitle}</h1>
          <p class="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-3xl">This is a read-only appearance preview of the current draft. Nothing has been saved, published or versioned by opening this tab.</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-7 max-w-4xl">
          <div class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5"><p class="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Version</p><p class="text-sm font-semibold text-slate-900">Draft preview</p></div>
          <div class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5"><p class="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Effective date</p><p class="text-sm font-semibold text-slate-900">Not yet published</p></div>
          <div class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5"><p class="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Published</p><p class="text-sm font-semibold text-slate-900">Not yet published</p></div>
        </div>
      </section>
      <section class="px-6 sm:px-9 py-3.5 bg-blue-50 border-b border-blue-100"><p class="text-xs text-blue-800 font-medium">Preview only. Closing this tab has no effect on the draft.</p></section>
      <section class="px-6 sm:px-10 lg:px-14 py-10 sm:py-14">
        <div class="max-w-[820px] mx-auto">
          <div class="mb-8 pb-5 border-b border-slate-200"><p class="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">Document</p><p class="text-xs text-slate-500 mt-1">${safeTitle} · Draft preview</p></div>
          <div>${publishedBody}</div>
        </div>
      </section>
      <footer class="border-t border-slate-200 px-6 sm:px-9 py-7 text-center"><p class="text-xs text-slate-600 leading-relaxed">Unpublished appearance preview generated by the BPA Solutions Legal Vault.</p><p class="text-xs text-slate-600 leading-relaxed mt-2">BPA Solutions (Business Process Automation Solutions) is a trading name of Anish Malhotra Pty Ltd.</p></footer>
    </article>
  </main>
</body>
</html>`);
    previewWindow.document.close();
  };

  return (
    <div className={`flex flex-col border border-slate-200 rounded-lg overflow-hidden bg-white ${isFullscreen ? 'fixed inset-0 z-[1200]' : ''}`}>
      <div className="bg-white border-b border-slate-200 shadow-[0_1px_0_rgba(15,23,42,0.02)]">
        <div className="flex flex-wrap items-center gap-1.5 px-3 py-2.5">
          {!readOnly && (
            <>
              <select value={blockTypeChoice} disabled={formattingDisabled} onMouseDown={captureSelection} onChange={(event)=>applyBlockType(event.target.value)} title={formattingDisabled ? 'Switch to Edit or Split to change paragraph style' : 'Paragraph style'} className={`${toolbarSelectClass} w-[132px]`}>
                {BLOCK_TYPE_OPTIONS.map((option)=><option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select value={fontChoice} disabled={formattingDisabled} onMouseDown={captureSelection} onChange={(event)=>{const value=event.target.value;setFontChoice(value);applyBlockStyle('font',value);}} title={formattingDisabled ? 'Switch to Edit or Split to apply a font' : 'Font family'} className={`${toolbarSelectClass} w-[170px]`}>
                {FONT_OPTIONS.map((option)=><option key={option.value || 'default'} value={option.value}>{option.label}</option>)}
              </select>
              <select value={sizeChoice} disabled={formattingDisabled} onMouseDown={captureSelection} onChange={(event)=>{const value=event.target.value;setSizeChoice(value);applyBlockStyle('size',value);}} title={formattingDisabled ? 'Switch to Edit or Split to change size' : 'Font size'} className={`${toolbarSelectClass} w-[76px]`}>
                {SIZE_OPTIONS.map((value)=><option key={value || 'default'} value={value}>{value ? `${value}px` : 'Size'}</option>)}
              </select>
              <select value={colorChoice} disabled={formattingDisabled} onMouseDown={captureSelection} onChange={(event)=>{const value=event.target.value;setColorChoice(value);applyBlockStyle('color',value);}} title={formattingDisabled ? 'Switch to Edit or Split to change colour' : 'Text colour'} className={`${toolbarSelectClass} w-[112px]`}>
                {COLOR_PRESETS.map((option)=><option key={option.value || 'default'} value={option.value}>{option.label}</option>)}
              </select>
              <select value={spacingChoice} disabled={formattingDisabled} onMouseDown={captureSelection} onChange={(event)=>applySpacingChoice(event.target.value)} title={formattingDisabled ? 'Switch to Edit or Split to change paragraph spacing' : 'Line and paragraph spacing'} className={`${toolbarSelectClass} w-[158px]`}>
                <option value="">Spacing</option>
                <optgroup label="Line spacing">
                  {LINE_SPACING_OPTIONS.map((value)=><option key={value} value={`line:${value}`}>{value}</option>)}
                </optgroup>
                <optgroup label="Paragraph spacing">
                  <option value="before:add">Add space before</option>
                  <option value="before:remove">Remove space before</option>
                  <option value="after:add">Add space after</option>
                  <option value="after:remove">Remove space after</option>
                  <option value="spacing:reset">Reset spacing</option>
                </optgroup>
              </select>

              <span className={dividerClass} />
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('bold')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Bold'} className={toolbarButtonClass}><SafeIcon icon={FiBold} /></button>
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('italic')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Italic'} className={toolbarButtonClass}><SafeIcon icon={FiItalic} /></button>
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('link')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Insert link'} className={toolbarButtonClass}><SafeIcon icon={FiLink} /></button>
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('quote')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Block quote'} className={toolbarButtonClass}><SafeIcon icon={FiMessageSquare} /></button>

              <span className={dividerClass} />
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('ul')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Bullet list'} className={toolbarButtonClass}><SafeIcon icon={FiList} /></button>
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('ol')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Numbered list'} className={toolbarButtonClass}><SafeIcon icon={FiListOrdered} /></button>
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={()=>handleToolbarAction('hr')} title={formattingDisabled ? 'Switch to Edit or Split to format' : 'Horizontal divider'} className={toolbarButtonClass}><SafeIcon icon={FiMinus} /></button>
              <button type="button" disabled={formattingDisabled} onMouseDown={preserveSelection} onClick={clearBlockFormatting} title={formattingDisabled ? 'Switch to Edit or Split to clear formatting' : 'Clear font, size, colour and spacing'} className={toolbarButtonClass}><SafeIcon icon={FiXCircle} /></button>
            </>
          )}

          <div className={`${readOnly ? 'ml-auto' : 'w-full xl:w-auto xl:ml-auto'} flex flex-wrap items-center justify-end gap-1.5 ${readOnly ? '' : 'pt-1 xl:pt-0'}`}>
            <button type="button" onClick={openPublishedPreview} className="h-9 inline-flex items-center px-3 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-slate-800 transition-colors shadow-sm" title="Open a read-only published appearance preview in a new tab"><SafeIcon icon={FiExternalLink} className="mr-1.5" />Published Preview</button>
            {!readOnly && (
              <div className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5">
                <button type="button" onClick={()=>setMode(mode==='edit'?'preview':'edit')} className={`h-8 px-2.5 rounded text-xs font-medium transition-colors ${mode==='preview'?'bg-white text-blue-700 shadow-sm':'text-slate-600 hover:text-slate-900'}`}><SafeIcon icon={mode==='preview'?FiEdit:FiEye} className="inline mr-1" />{mode==='preview'?'Edit':'Preview'}</button>
                <button type="button" onClick={()=>setMode('split')} className={`h-8 px-2.5 rounded text-xs font-medium transition-colors ${mode==='split'?'bg-white text-blue-700 shadow-sm':'text-slate-600 hover:text-slate-900'}`}><SafeIcon icon={FiEye} className="inline mr-1" />Split</button>
              </div>
            )}
            <button type="button" onClick={()=>setIsFullscreen(!isFullscreen)} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition-colors" title={isFullscreen?'Exit Fullscreen':'Fullscreen'}><SafeIcon icon={isFullscreen?FiMinimize:FiMaximize} className="text-slate-600" /></button>
          </div>
        </div>
      </div>

      {!readOnly && <div className={`px-3 py-2 text-[11px] border-b ${mode==='preview' ? 'bg-amber-50 text-amber-800 border-amber-100' : 'bg-white text-slate-500 border-slate-100'}`}>{mode==='preview' ? 'Editor Preview is read-only. Click Edit or Split before applying formatting.' : 'Formatting and spacing apply to the selected paragraph(s) or current paragraph. Use Published Preview for the final publication-style view.'}</div>}

      <div className={`flex ${mode==='split'?'flex-col md:flex-row':'flex-col'} ${isFullscreen?'flex-1 min-h-0':'h-[52dvh] min-h-[360px] md:min-h-[440px] md:max-h-[720px]'}`}>
        {(mode==='edit'||mode==='split') && <div className={`${mode==='split'?'w-full h-1/2 md:w-1/2 md:h-full':'flex-1'} min-w-0 min-h-0 border-b md:border-b-0 md:border-r border-slate-200`}><textarea ref={textareaRef} value={content} onChange={(event)=>{selectionRef.current={start:event.target.selectionStart,end:event.target.selectionEnd};onChange(event.target.value);}} onSelect={captureSelection} onClick={captureSelection} onKeyUp={captureSelection} onBlur={captureSelection} onPaste={handlePaste} disabled={readOnly} className="w-full h-full p-4 font-mono text-sm resize-none outline-none bg-white" placeholder="Start writing your document here... Use Markdown for headings and basic formatting." /></div>}
        {(mode==='preview'||mode==='split') && <div className={`${mode==='split'?'w-full h-1/2 md:w-1/2 md:h-full':'flex-1'} min-w-0 min-h-0 overflow-auto bg-white`}><div className="p-5 sm:p-6"><div className="w-full max-w-none" dangerouslySetInnerHTML={{__html:renderMarkdownBlocks(content)}} /></div></div>}
      </div>

      {linkDialog.open && <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[1600] p-4"><div className="bg-white rounded-xl p-6 shadow-xl border border-slate-200 w-full max-w-md"><h3 className="text-lg font-semibold mb-4">Insert Link</h3><div className="space-y-4"><div><label className="block text-sm font-medium text-slate-700 mb-1">Link Text</label><input type="text" value={linkDialog.text} onChange={(event)=>setLinkDialog({...linkDialog,text:event.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Link text" /></div><div><label className="block text-sm font-medium text-slate-700 mb-1">URL</label><input type="url" value={linkDialog.url} onChange={(event)=>setLinkDialog({...linkDialog,url:event.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="https://example.com" autoFocus /></div></div><div className="flex justify-end space-x-3 mt-6"><button type="button" onClick={()=>setLinkDialog({open:false,text:'',url:''})} className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors">Cancel</button><button type="button" onClick={insertLink} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">Insert Link</button></div></div></div>}
    </div>
  );
}